// src/pages/ProposalView.jsx
// Page PUBLIQUE d'une proposition d'acquisition (lien par jeton, sans compte).
// Le client parcourt les œuvres, en compare 2 ou 3, réagit, puis envoie ses réponses.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import { callProposal, REACTION_LABELS } from "@/lib/proposalApi";
import { useFT } from "@/locales/foundationCopy";

const money = (n, cur, T, locale) =>
  n == null ? T("Prix sur demande") : `${Number(n).toLocaleString(locale)} ${cur === "EUR" || !cur ? "€" : cur}`;

// Prix selon le mode du lien : none (rien du tout), incl (TTC), excl (HT), legacy (anciens liens : prix demandé).
// En mode none le serveur n'envoie aucun montant ; ici, on n'affiche donc rien.
function PriceBlock({ item, mode, big }) {
  const { T, locale } = useFT();
  if (mode === "none") return null;
  const amount = money(item.asking_price, item.currency, T, locale);
  const tax = item.price_tax === "incl" ? " " + T("TTC") : item.price_tax === "excl" ? " " + T("HT") : "";
  return (
    <>
      <p className={`mt-${big ? 4 : 3} ${big ? "text-2xl" : "text-xl"}`}>{item.asking_price == null ? amount : `${amount}${tax}`}</p>
      <p className="text-xs text-[#8a8378]">{mode === "legacy" ? T("Prix demandé") : T("Prix")}</p>
    </>
  );
}

const Rich = ({ text }) => text.split(/(<b>.*?<\/b>)/g).map((p, i) => (p.startsWith("<b>") ? <strong key={i}>{p.slice(3, -4)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>));

const REACTION_STYLE = {
  interested: { on: "bg-[#1a1a1a] text-[#f9f7f4] border-[#1a1a1a]" },
  not_for_me: { on: "bg-[#6b6459] text-white border-[#6b6459]" },
  question: { on: "bg-[#c9a961] text-[#1a1a1a] border-[#c9a961]" },
};

function ReactionBar({ item, reaction, disabled, onSave }) {
  const { T } = useFT();
  const [comment, setComment] = useState(reaction?.comment || "");
  useEffect(() => setComment(reaction?.comment || ""), [reaction?.comment, item.id]);
  return (
    <div className="mt-4">
      <div className="flex flex-wrap gap-2">
        {Object.entries(REACTION_LABELS).map(([key, label]) => {
          const active = reaction?.reaction === key;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onSave(item.id, key, comment)}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                active ? REACTION_STYLE[key].on : "border-[#e5e0d8] text-[#4a453d] hover:bg-[#f9f7f4]"
              } disabled:opacity-60`}
            >
              {T(label)}
            </button>
          );
        })}
      </div>
      {reaction && (
        <textarea
          value={comment}
          disabled={disabled}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => comment !== (reaction?.comment || "") && onSave(item.id, reaction.reaction, comment)}
          placeholder={T("Un commentaire pour votre conseiller (facultatif)")}
          rows={2}
          className="mt-3 w-full px-3 py-2 border border-[#e5e0d8] rounded-lg text-sm bg-white"
        />
      )}
    </div>
  );
}

function ItemImage({ item, className = "" }) {
  const { T } = useFT();
  return item.image_url ? (
    <img src={item.image_url} alt={item.title || ""} className={`w-full object-contain bg-[#f3f0ea] ${className}`} />
  ) : (
    <div className={`w-full bg-[#f3f0ea] flex items-center justify-center text-[#b9b2a4] text-sm ${className}`}>
      {T("Pas d'image")}
    </div>
  );
}

export default function ProposalView() {
  const { token } = useParams();
  const { T, locale, lang } = useFT();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [reactions, setReactions] = useState({});
  const [mode, setMode] = useState("browse");
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let alive = true;
    callProposal({ token })
      .then((data) => {
        if (!alive) return;
        setState({ loading: false, error: null, data });
        setReactions(data.reactions || {});
      })
      .catch((e) => alive && setState({ loading: false, error: e.message, data: null }));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => { document.title = T("Proposition d'acquisition"); }, [T]);

  const items = useMemo(() => state.data?.items || [], [state.data]);
  const submitted = Boolean(state.data?.proposal?.submitted_at);
  const answered = Object.keys(reactions).length;

  const save = useCallback(async (artworkId, reaction, comment) => {
    setReactions((r) => ({ ...r, [artworkId]: { reaction, comment } }));
    try {
      await callProposal({ token, action: "react", artwork_id: artworkId, reaction, comment });
      setNotice(null);
    } catch (e) {
      setNotice(e.message);
    }
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await callProposal({ token, action: "submit", lang });
      setState((s) => ({ ...s, data: { ...s.data, proposal: { ...s.data.proposal, submitted_at: new Date().toISOString() } } }));
      setNotice(null);
    } catch (e) {
      setNotice(e.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 3 ? p : [...p, id]));

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4] text-[#6b6459] font-serif">{T("Chargement…")}</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4] p-6 text-center font-serif">
        <div>
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <p className="mt-6 text-lg text-[#2a261f]">{T(state.error)}</p>
          <p className="mt-2 text-sm text-[#8a8378]">{T("Demandez un nouveau lien à votre conseiller.")}</p>
        </div>
      </div>
    );
  }

  const { proposal } = state.data;
  const current = items[index];
  const compared = items.filter((i) => picked.includes(i.id));
  const expires = new Date(proposal.expires_at).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <header className="border-b border-[#e5e0d8] bg-[#f9f7f4]/95">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <span className="text-xs text-[#8a8378]">{T("Lien valable jusqu'au {date}", { date: expires })}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <p className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">{T("Proposition d'acquisition")}</p>
        <h1 className="text-3xl md:text-4xl font-bold">{proposal.title}</h1>
        <p className="mt-2 text-[#4a453d]">
          {proposal.advisor_name ? T("Sélectionnée par {name}", { name: proposal.advisor_name }) : T("Sélection de votre conseiller")}
          {proposal.client_label ? " " + T("pour {name}", { name: proposal.client_label }) : ""}.
        </p>
        <p className="mt-4 text-sm text-[#6b6459] border-l-2 border-[#c9a961] pl-4 max-w-3xl">
          <Rich text={T("Ces œuvres sont <b>proposées par votre conseiller et ne sont pas détenues</b>. Le prix indiqué est le prix demandé. Votre réaction n'engage à rien ; la suite se décide avec votre conseiller.")} />
        </p>

        {submitted && (
          <div className="mt-8 bg-[#eef5f1] border border-[#cfe3d8] rounded-xl p-6">
            <div className="text-xl">✓ {T("Vos réponses ont été envoyées à votre conseiller.")}</div>
            <p className="text-sm text-[#4a453d] mt-1">{T("Merci. Vous pouvez encore consulter la proposition jusqu'au {date}.", { date: expires })}</p>
          </div>
        )}
        {notice && <p className="mt-6 text-sm text-[#dc2626]">{notice}</p>}

        {items.length === 0 ? (
          <p className="mt-10 text-[#6b6459]">{T("Cette proposition ne contient pas encore d'œuvre.")}</p>
        ) : (
          <>
            <div className="mt-8 flex gap-2 border-b border-[#e5e0d8]">
              {[["browse", T("Œuvre par œuvre")], ["compare", T("Comparer")]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  className={`px-4 py-2 text-sm -mb-px border-b-2 ${mode === k ? "border-[#1a1a1a] font-semibold" : "border-transparent text-[#6b6459]"}`}
                >
                  {l}
                </button>
              ))}
            </div>

            {mode === "browse" && current && (
              <section className="mt-8 grid md:grid-cols-2 gap-8">
                <div>
                  <ItemImage item={current} className="max-h-[520px] min-h-[280px]" />
                  <div className="mt-3 flex items-center justify-between text-sm text-[#6b6459]">
                    <button disabled={index === 0} onClick={() => setIndex(index - 1)} className="px-3 py-1.5 border border-[#e5e0d8] rounded-lg disabled:opacity-40">{T("← Précédente")}</button>
                    <span>{index + 1} / {items.length}</span>
                    <button disabled={index === items.length - 1} onClick={() => setIndex(index + 1)} className="px-3 py-1.5 border border-[#e5e0d8] rounded-lg disabled:opacity-40">{T("Suivante →")}</button>
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{current.title}</h2>
                  <p className="text-lg text-[#4a453d]">{current.artist}{current.year ? `, ${current.year}` : ""}</p>
                  <p className="mt-2 text-sm text-[#6b6459]">{[current.medium, current.dimensions].filter(Boolean).join(" · ")}</p>
                  <PriceBlock item={current} mode={proposal.price_mode || "legacy"} big />
                  {current.rationale && (
                    <div className="mt-5">
                      <div className="text-xs tracking-[0.14em] uppercase text-[#8a8378] mb-1">{T("Pourquoi cette œuvre")}</div>
                      <p className="text-[15px] leading-relaxed text-[#2a261f] whitespace-pre-wrap">{current.rationale}</p>
                    </div>
                  )}
                  <ReactionBar item={current} reaction={reactions[current.id]} disabled={submitted} onSave={save} />
                  <label className="mt-6 inline-flex items-center gap-2 text-sm text-[#4a453d] cursor-pointer">
                    <input type="checkbox" checked={picked.includes(current.id)} onChange={() => togglePick(current.id)} />
                    {T("Ajouter à la comparaison")} {picked.length >= 3 && !picked.includes(current.id) ? T("(3 maximum)") : ""}
                  </label>
                </div>
              </section>
            )}

            {mode === "compare" && (
              <section className="mt-8">
                {compared.length < 2 ? (
                  <div>
                    <p className="text-[#4a453d]">{T("Choisissez 2 ou 3 œuvres à comparer.")}</p>
                    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.map((i) => (
                        <label key={i.id} className={`bg-white border rounded-xl p-3 cursor-pointer ${picked.includes(i.id) ? "border-[#1a1a1a]" : "border-[#e5e0d8]"}`}>
                          <ItemImage item={i} className="h-40" />
                          <div className="mt-2 flex items-start gap-2 text-sm">
                            <input type="checkbox" className="mt-1" checked={picked.includes(i.id)} onChange={() => togglePick(i.id)} />
                            <span><strong>{i.title}</strong><br />{i.artist}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(${compared.length}, minmax(240px, 1fr))` }}>
                      {compared.map((i) => (
                        <div key={i.id} className="bg-white border border-[#e5e0d8] rounded-2xl p-4 flex flex-col">
                          <ItemImage item={i} className="h-56 rounded-lg" />
                          <h3 className="mt-3 text-lg font-bold">{i.title}</h3>
                          <p className="text-[#4a453d]">{i.artist}{i.year ? `, ${i.year}` : ""}</p>
                          <dl className="mt-3 text-sm space-y-1 text-[#4a453d]">
                            <div><dt className="inline text-[#8a8378]">{T("Technique")} : </dt><dd className="inline">{i.medium || "—"}</dd></div>
                            <div><dt className="inline text-[#8a8378]">{T("Dimensions")} : </dt><dd className="inline">{i.dimensions || "—"}</dd></div>
                          </dl>
                          <PriceBlock item={i} mode={proposal.price_mode || "legacy"} />
                          {i.rationale && <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{i.rationale}</p>}
                          <ReactionBar item={i} reaction={reactions[i.id]} disabled={submitted} onSave={save} />
                          <button onClick={() => togglePick(i.id)} className="mt-4 text-xs text-[#8a8378] underline self-start">{T("Retirer de la comparaison")}</button>
                        </div>
                      ))}
                    </div>
                    {compared.length < 3 && <p className="mt-4 text-sm text-[#8a8378]">{T("Vous pouvez ajouter une troisième œuvre depuis « Œuvre par œuvre ».")}</p>}
                  </div>
                )}
              </section>
            )}

            {!submitted && (
              <div className="mt-12 border-t border-[#e5e0d8] pt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-[#6b6459]">{T("{a} œuvre(s) sur {n} avec une réponse.", { a: answered, n: items.length })}</p>
                <button
                  onClick={submit}
                  disabled={busy || answered === 0}
                  className="px-6 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#b9975a] disabled:opacity-50"
                >
                  {busy ? T("Envoi…") : T("Envoyer mes réponses à mon conseiller")}
                </button>
              </div>
            )}
          </>
        )}

        <p className="mt-12 text-xs text-[#8a8378] max-w-3xl">
          {T("Les informations sont fournies par votre conseiller. Aucune estimation n'est affichée ici. La transaction et le prix définitif se décident entre vous et votre conseiller, hors plateforme.")}
        </p>
      </main>
    </div>
  );
}
